import {createSystem, defaultConfig, defineConfig} from '@chakra-ui/react';
import {
    alertAnatomy,
    checkboxAnatomy,
    dateInputAnatomy,
    datePickerAnatomy,
    dialogAnatomy,
    fieldAnatomy,
    listAnatomy,
    menuAnatomy,
    switchAnatomy,
    tableAnatomy,
    tagAnatomy,
} from '@chakra-ui/react/anatomy';

// Chakra UI v3 with the look of the v2 default theme the app was designed with: v2's colour palettes,
// fonts and body text colour, plus recipe overrides for every component whose v3 defaults look
// different (sizes, spacing, colours). Values are copied from @chakra-ui/theme 2.x.

const v2Fonts = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

const v2Colors = {
    black: {value: '#000000'},
    whiteAlpha: {50: {value: 'rgba(255, 255, 255, 0.04)'}, 100: {value: 'rgba(255, 255, 255, 0.06)'}, 200: {value: 'rgba(255, 255, 255, 0.08)'}, 300: {value: 'rgba(255, 255, 255, 0.16)'}, 400: {value: 'rgba(255, 255, 255, 0.24)'}, 500: {value: 'rgba(255, 255, 255, 0.36)'}, 600: {value: 'rgba(255, 255, 255, 0.48)'}, 700: {value: 'rgba(255, 255, 255, 0.64)'}, 800: {value: 'rgba(255, 255, 255, 0.80)'}, 900: {value: 'rgba(255, 255, 255, 0.92)'}},
    blackAlpha: {50: {value: 'rgba(0, 0, 0, 0.04)'}, 100: {value: 'rgba(0, 0, 0, 0.06)'}, 200: {value: 'rgba(0, 0, 0, 0.08)'}, 300: {value: 'rgba(0, 0, 0, 0.16)'}, 400: {value: 'rgba(0, 0, 0, 0.24)'}, 500: {value: 'rgba(0, 0, 0, 0.36)'}, 600: {value: 'rgba(0, 0, 0, 0.48)'}, 700: {value: 'rgba(0, 0, 0, 0.64)'}, 800: {value: 'rgba(0, 0, 0, 0.80)'}, 900: {value: 'rgba(0, 0, 0, 0.92)'}},
    gray: {50: {value: '#F7FAFC'}, 100: {value: '#EDF2F7'}, 200: {value: '#E2E8F0'}, 300: {value: '#CBD5E0'}, 400: {value: '#A0AEC0'}, 500: {value: '#718096'}, 600: {value: '#4A5568'}, 700: {value: '#2D3748'}, 800: {value: '#1A202C'}, 900: {value: '#171923'}},
    red: {50: {value: '#FFF5F5'}, 100: {value: '#FED7D7'}, 200: {value: '#FEB2B2'}, 300: {value: '#FC8181'}, 400: {value: '#F56565'}, 500: {value: '#E53E3E'}, 600: {value: '#C53030'}, 700: {value: '#9B2C2C'}, 800: {value: '#822727'}, 900: {value: '#63171B'}},
    orange: {50: {value: '#FFFAF0'}, 100: {value: '#FEEBC8'}, 200: {value: '#FBD38D'}, 300: {value: '#F6AD55'}, 400: {value: '#ED8936'}, 500: {value: '#DD6B20'}, 600: {value: '#C05621'}, 700: {value: '#9C4221'}, 800: {value: '#7B341E'}, 900: {value: '#652B19'}},
    yellow: {50: {value: '#FFFFF0'}, 100: {value: '#FEFCBF'}, 200: {value: '#FAF089'}, 300: {value: '#F6E05E'}, 400: {value: '#ECC94B'}, 500: {value: '#D69E2E'}, 600: {value: '#B7791F'}, 700: {value: '#975A16'}, 800: {value: '#744210'}, 900: {value: '#5F370E'}},
    green: {50: {value: '#F0FFF4'}, 100: {value: '#C6F6D5'}, 200: {value: '#9AE6B4'}, 300: {value: '#68D391'}, 400: {value: '#48BB78'}, 500: {value: '#38A169'}, 600: {value: '#2F855A'}, 700: {value: '#276749'}, 800: {value: '#22543D'}, 900: {value: '#1C4532'}},
    teal: {50: {value: '#E6FFFA'}, 100: {value: '#B2F5EA'}, 200: {value: '#81E6D9'}, 300: {value: '#4FD1C5'}, 400: {value: '#38B2AC'}, 500: {value: '#319795'}, 600: {value: '#2C7A7B'}, 700: {value: '#285E61'}, 800: {value: '#234E52'}, 900: {value: '#1D4044'}},
    blue: {50: {value: '#ebf8ff'}, 100: {value: '#bee3f8'}, 200: {value: '#90cdf4'}, 300: {value: '#63b3ed'}, 400: {value: '#4299e1'}, 500: {value: '#3182ce'}, 600: {value: '#2b6cb0'}, 700: {value: '#2c5282'}, 800: {value: '#2a4365'}, 900: {value: '#1A365D'}},
    cyan: {50: {value: '#EDFDFD'}, 100: {value: '#C4F1F9'}, 200: {value: '#9DECF9'}, 300: {value: '#76E4F7'}, 400: {value: '#0BC5EA'}, 500: {value: '#00B5D8'}, 600: {value: '#00A3C4'}, 700: {value: '#0987A0'}, 800: {value: '#086F83'}, 900: {value: '#065666'}},
    purple: {50: {value: '#FAF5FF'}, 100: {value: '#E9D8FD'}, 200: {value: '#D6BCFA'}, 300: {value: '#B794F4'}, 400: {value: '#9F7AEA'}, 500: {value: '#805AD5'}, 600: {value: '#6B46C1'}, 700: {value: '#553C9A'}, 800: {value: '#44337A'}, 900: {value: '#322659'}},
    pink: {50: {value: '#FFF5F7'}, 100: {value: '#FED7E2'}, 200: {value: '#FBB6CE'}, 300: {value: '#F687B3'}, 400: {value: '#ED64A6'}, 500: {value: '#D53F8C'}, 600: {value: '#B83280'}, 700: {value: '#97266D'}, 800: {value: '#702459'}, 900: {value: '#521B41'}},
};

const config = defineConfig({
    theme: {
        keyframes: {
            'checkmark-draw': {
                from: {strokeDashoffset: '23', opacity: '0'},
                to: {strokeDashoffset: '0', opacity: '1'},
            },
        },
        tokens: {
            colors: v2Colors,
            fonts: {
                heading: {value: v2Fonts},
                body: {value: v2Fonts},
            },
        },
        semanticTokens: {
            colors: {
                fg: {DEFAULT: {value: '{colors.gray.800}'}},
            },
            // v2 rounded buttons, inputs, menus and dialogs with 'md' (6px); v3 uses 4px for most.
            radii: {
                l2: {value: '{radii.md}'},
                l3: {value: '{radii.md}'},
            },
        },
        recipes: {
            heading: {
                base: {fontWeight: 'bold'},
                variants: {
                    size: {
                        xs: {textStyle: 'unset', fontSize: 'sm', lineHeight: '1.2'},
                        sm: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.2'},
                        md: {textStyle: 'unset', fontSize: 'xl', lineHeight: '1.2'},
                        lg: {textStyle: 'unset', fontSize: {base: '2xl', md: '3xl'}, lineHeight: {base: '1.33', md: '1.2'}},
                        xl: {textStyle: 'unset', fontSize: {base: '3xl', md: '4xl'}, lineHeight: {base: '1.33', md: '1.2'}},
                    },
                },
            },
            // v2's inputs: 16px text with 16px padding, gray.300 border on hover, blue focus border and ring.
            input: {
                variants: {
                    size: {md: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.5', px: '4'}},
                    variant: {outline: {focusRingColor: 'blue.500', _hover: {borderColor: 'gray.300'}, _focusVisible: {borderColor: 'blue.500'}}},
                },
            },
            // v2's input addons ("400/"): body-size text with the input's padding.
            inputAddon: {
                variants: {size: {md: {textStyle: 'unset', fontSize: 'md', px: '4'}}},
            },
            // v2's keyboard keys: bold at 0.8em with a 3px bottom border.
            kbd: {
                base: {fontSize: '0.8em', fontWeight: 'bold', lineHeight: 'normal', px: '0.4em', borderRadius: 'md', whiteSpace: 'nowrap'},
                variants: {
                    size: {md: {textStyle: 'unset', height: 'auto'}},
                    variant: {raised: {borderWidth: '1px', borderBottomWidth: '3px', borderColor: 'gray.200', bg: 'gray.100'}},
                },
            },
            // v2's inline code: 14px monospace in the surrounding line height, a small radius.
            code: {
                base: {borderRadius: 'xs', lineHeight: 'inherit'},
                variants: {size: {sm: {textStyle: 'unset', fontSize: 'sm', px: '0.2em', minH: 'auto'}}},
            },
            // v2's badges: bold uppercase 12px text with a 2px radius; solid ones in the 500 shade.
            badge: {
                base: {fontWeight: 'bold', textTransform: 'uppercase', borderRadius: 'xs'},
                variants: {
                    size: {sm: {textStyle: 'unset', fontSize: 'xs', lineHeight: '1.5', minH: 'auto', px: '1'}},
                    variant: {solid: {bg: 'colorPalette.500', color: 'white'}},
                },
            },
            button: {
                // v2 buttons had no border except the outline variant (which sets its own).
                // Lucide icons keep the size they are given; v3 would shrink them to the button size's icon box.
                base: {fontWeight: 'semibold', borderWidth: '0', '& svg.lucide': {width: 'auto', height: 'auto'}},
                variants: {
                    size: {
                        xs: {h: '6', minW: '6', textStyle: 'unset', fontSize: 'xs', px: '2'},
                        sm: {h: '8', minW: '8', textStyle: 'unset', fontSize: 'sm', px: '3'},
                        md: {h: '10', minW: '10', textStyle: 'unset', fontSize: 'md', px: '4'},
                        lg: {h: '12', minW: '12', textStyle: 'unset', fontSize: 'lg', px: '6'},
                    },
                    variant: {
                        // v2's solid buttons: the palette's 500 shade, 600 on hover, 700 when pressed.
                        solid: {
                            bg: 'colorPalette.500',
                            color: 'white',
                            _hover: {bg: 'colorPalette.600'},
                            _active: {bg: 'colorPalette.700'},
                        },
                    },
                },
            },
        },
        slotRecipes: {
            // The segmented date field (album creation) looks like v2's inputs; the focused segment is
            // highlighted like in the browser's own date field.
            dateInput: {
                slots: dateInputAnatomy.keys(),
                base: {
                    root: {gap: '0'},
                    label: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.5', fontWeight: 'medium', me: '3', mb: '2'},
                    segment: {'&[data-type=literal]': {color: 'inherit'}},
                },
                variants: {
                    size: {md: {segmentGroup: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.5', px: '4'}}},
                    variant: {
                        outline: {
                            segmentGroup: {
                                _hover: {borderColor: 'gray.300'},
                                _focusWithin: {borderColor: 'blue.500', boxShadow: '0 0 0 1px {colors.blue.500}'},
                            },
                            segment: {_focus: {bg: 'blue.500', color: 'white'}},
                        },
                    },
                },
            },
            // v2's small switch: a 26x16 track, gray.300 off and the palette's 500 shade on.
            switch: {
                slots: switchAnatomy.keys(),
                base: {label: {lineHeight: '1.5'}},
                variants: {
                    size: {sm: {root: {'--switch-width': '26px', '--switch-height': '16px'}}},
                    variant: {solid: {control: {bg: 'gray.300', _checked: {bg: 'colorPalette.500'}}}},
                },
            },
            // v2's bullet and numbered lists are indented by 1em.
            list: {
                slots: listAnatomy.keys(),
                variants: {variant: {marker: {root: {ms: '1em'}}}},
            },
            // The date field looks like v2's other form fields (see field and input).
            datePicker: {
                slots: datePickerAnatomy.keys(),
                base: {
                    root: {gap: '0'},
                    label: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.5', me: '3', mb: '2'},
                    input: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.5'},
                },
                variants: {size: {md: {root: {'--datepicker-input-px': 'sizes.4'}}}},
            },
            // v2's small table cells: 16px line height, 16px side padding.
            table: {
                slots: tableAnatomy.keys(),
                variants: {size: {sm: {cell: {px: '4', py: '2', fontSize: 'sm', lineHeight: '1rem'}}}},
            },
            // v2's tags: subtle by default (palette 100 background, 800 text), 14px medium text.
            tag: {
                slots: tagAnatomy.keys(),
                base: {
                    root: {fontWeight: 'medium', borderRadius: 'md'},
                    closeTrigger: {boxSize: '5', borderRadius: 'full', opacity: 0.5, _hover: {opacity: 0.8}, '& svg': {boxSize: '4'}, cursor: 'pointer'},
                },
                variants: {
                    size: {
                        md: {
                            root: {px: '2', minH: '6', gap: '0.5', '--tag-element-size': 'sizes.5', '--tag-element-offset': '-4px'},
                            label: {textStyle: 'unset', fontSize: 'sm'},
                        },
                    },
                    variant: {subtle: {root: {color: 'colorPalette.800'}}},
                },
                defaultVariants: {variant: 'subtle'},
            },
            // v2's FormControl: a full-width 16px label 8px above its input.
            field: {
                slots: fieldAnatomy.keys(),
                base: {
                    root: {gap: '0'},
                    label: {textStyle: 'unset', fontSize: 'md', lineHeight: '1.5', alignSelf: 'stretch', me: '3', mb: '2'},
                },
                variants: {orientation: {vertical: {root: {alignItems: 'stretch'}}}},
            },
            // v2's subtle alert: text in the inherited colour and size (line height 1.5), 12px/16px
            // padding, the icon in the status colour's 600 shade.
            alert: {
                slots: alertAnatomy.keys(),
                base: {
                    root: {lineHeight: '1.5'},
                    title: {fontWeight: 'normal'},
                    indicator: {height: '6'},
                },
                variants: {
                    variant: {subtle: {root: {color: 'inherit'}, indicator: {color: 'colorPalette.600'}}},
                    size: {md: {root: {py: '3', textStyle: 'unset', fontSize: 'md'}}},
                },
            },
            // v2's checkbox: a 16px box with a 2px border, checked in the 500 shade, a normal 16px label.
            checkbox: {
                slots: checkboxAnatomy.keys(),
                base: {
                    label: {fontWeight: 'normal'},
                    // The tick draws itself in, like v2's.
                    control: {borderWidth: '2px', '& polyline': {strokeDasharray: '23', animation: 'checkmark-draw 200ms ease-out'}},
                },
                variants: {
                    size: {md: {root: {gap: '2'}, control: {boxSize: '4'}}},
                    variant: {
                        solid: {
                            control: {
                                borderColor: 'gray.200',
                                _checked: {bg: 'colorPalette.500', borderColor: 'colorPalette.500'},
                            },
                        },
                    },
                },
            },
            menu: {
                slots: menuAnatomy.keys(),
                base: {
                    content: {boxShadow: 'sm'},
                },
                variants: {
                    size: {
                        md: {
                            content: {px: '0'}  // menu buttons span the whole width
                        },
                    },
                },
            },
            // v2's Modal: darker overlay, smaller radius, body-size text, v2 paddings and size scale.
            dialog: {
                slots: dialogAnatomy.keys(),
                base: {
                    // v2's open/close motion: 200ms ease-out in, 100ms ease-in out, the backdrop in step
                    // with the dialog (v3 eases with CSS's default curve and fades the backdrop slower).
                    backdrop: {
                        bg: 'blackAlpha.600',
                        _open: {animationDuration: 'moderate', animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)'},
                        _closed: {animationDuration: 'faster', animationTimingFunction: 'cubic-bezier(0.4, 0, 1, 1)'},
                    },
                    // Inherit a unitless 1.5 line height like v2, so smaller text inside keeps its own.
                    content: {
                        borderRadius: 'md',
                        textStyle: 'unset',
                        fontSize: 'md',
                        lineHeight: '1.5',
                        _open: {animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)'},
                        _closed: {animationTimingFunction: 'cubic-bezier(0.4, 0, 1, 1)'},
                    },
                    header: {py: '4'},
                    title: {textStyle: 'xl'},
                    body: {py: '2'},
                    footer: {py: '4'},
                    closeTrigger: {insetEnd: '3'},
                },
                variants: {
                    size: {
                        xs: {content: {maxW: 'xs'}},
                        sm: {content: {maxW: 'sm'}},
                        md: {content: {maxW: 'md'}},
                        lg: {content: {maxW: 'lg'}},
                        xl: {content: {maxW: 'xl'}},
                    },
                },
            },
        },
    },
});

export const system = createSystem(defaultConfig, config);
